import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Loader2, CreditCard, Star } from "lucide-react";
import { toast } from "sonner";
import { Reveal, ImageReveal } from "../components/Reveal";
import { EditorialMarquee } from "../components/Marquee";
import { SERVICES, PROCESS, IMAGES, WEDDING_PACKAGES, CONTENT_PARTNER, ADDONS, DAY_IN_LIFE, TOP_TIER } from "../data/site";
import { createCheckoutSession, getPaymentConfig } from "../lib/api";

const PageHeader = ({ overline, title, sub }) => (
  <section className="mx-auto max-w-7xl px-6 pb-16 pt-40 lg:px-10 lg:pt-48" data-testid="page-header">
    <Reveal>
      <p className="overline text-sand-700">{overline}</p>
      <h1 className="mt-6 font-display text-6xl leading-[0.9] tracking-tight text-sand-100 md:text-8xl">{title}</h1>
      {sub && <p className="mt-8 max-w-xl font-body text-lg font-light text-sand-400">{sub}</p>}
    </Reveal>
  </section>
);

const AddOns = () => {
  const [loadingId, setLoadingId] = useState(null);

  const book = async (a) => {
    if (a.waveLink) {
      window.location.href = a.waveLink;
      return;
    }
    setLoadingId(a.id);
    try {
      const { url } = await createCheckoutSession(a.id);
      if (url) window.location.href = url;
      else throw new Error("no url");
    } catch {
      toast.error("Couldn't start checkout. Please try again or contact us.");
      setLoadingId(null);
    }
  };

  const fmt = (a) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(a);

  return (
    <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10" data-testid="addons">
      <Reveal className="mb-16 max-w-2xl">
        <p className="overline text-sand-700">À la carte</p>
        <h2 className="mt-6 font-display text-5xl text-sand-100 md:text-7xl">Quick add-ons</h2>
        <p className="mt-6 font-body leading-relaxed text-sand-400">
          Focused services you can book on demand — no retainer required.
        </p>
      </Reveal>

      <div className="grid gap-px border border-border md:grid-cols-2">
        {ADDONS.map((a, i) => (
          <motion.div
            key={a.id}
            className="flex flex-col bg-surface p-10 transition-colors duration-500 hover:bg-[#1d1815] lg:p-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            data-testid={`addon-card-${a.id}`}
          >
            <span className="font-display text-2xl italic text-sand-700">0{i + 1}</span>
            <h3 className="mt-4 font-display text-4xl text-sand-100">{a.name}</h3>
            <div className="mt-4 flex items-end gap-2">
              <span className="font-display text-5xl text-sand-100">{fmt(a.price)}</span>
              {a.unit && <span className="mb-2 font-body text-sm text-sand-400">{a.unit}</span>}
            </div>
            <p className="mt-6 flex-1 font-body leading-relaxed text-sand-100/90">{a.desc}</p>
            {a.note && (
              <p className="mt-4 border-l-2 border-sand-700 pl-3 font-body text-sm uppercase tracking-wide text-sand-700" data-testid={`addon-note-${a.id}`}>
                {a.note}
              </p>
            )}
            <button
              onClick={() => book(a)}
              disabled={loadingId === a.id}
              className="btn-desert mt-8 w-full justify-center"
              data-testid={`book-addon-${a.id}`}
            >
              {loadingId === a.id ? <><Loader2 size={16} className="animate-spin" /> Redirecting</> : <><CreditCard size={16} /> {a.waveLink ? "Book with Wave" : "Book online"}</>}
            </button>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const TopTier = () => {
  const [loadingId, setLoadingId] = useState(null);

  const book = async (id) => {
    setLoadingId(id);
    try {
      const { url } = await createCheckoutSession(id);
      if (url) window.location.href = url;
      else throw new Error("no url");
    } catch {
      toast.error("Couldn't start checkout. Please try again or contact us.");
      setLoadingId(null);
    }
  };

  const fmt = (a) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(a);

  return (
    <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10" data-testid="top-tier">
      <Reveal className="mb-16 max-w-2xl">
        <p className="overline text-sand-700">Top Tier — Commercial Production</p>
        <h2 className="mt-6 font-display text-5xl text-sand-100 md:text-7xl">The full campaign</h2>
        <p className="mt-6 font-body leading-relaxed text-sand-400">
          Development, production, post, and paid placement — we build the commercial and run the ad campaign across social media and popular streaming platforms to drive lead generation.
        </p>
      </Reveal>

      <div className="grid gap-px border border-border lg:grid-cols-2">
        {TOP_TIER.map((t, i) => (
          <motion.div
            key={t.id}
            className={`flex flex-col p-10 transition-colors duration-500 lg:p-14 ${t.featured ? "bg-[#1d1815]" : "bg-surface hover:bg-[#1d1815]"}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            data-testid={`toptier-card-${t.id}`}
          >
            <div className="flex items-center justify-between">
              <span className="font-display text-2xl italic text-sand-700">0{i + 1}</span>
              {t.featured && (
                <span className="flex items-center gap-1 border border-sand-700 px-3 py-1 text-[0.62rem] uppercase tracking-widest text-sand-700">
                  <Star size={11} fill="currentColor" /> Best value
                </span>
              )}
            </div>
            <h3 className="mt-6 font-display text-4xl text-sand-100 md:text-5xl">{t.name}</h3>
            <p className="mt-4 font-display text-6xl text-sand-100">{fmt(t.price)}</p>
            <p className="mt-4 font-body text-sm italic text-sand-400">{t.tagline}</p>
            <ul className="mt-8 flex-1 space-y-4">
              {t.includes.map((f) => (
                <li key={f} className="flex items-start gap-3 font-body text-base text-sand-100/90">
                  <Check size={18} className="mt-0.5 shrink-0 text-sand-700" /> {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => book(t.id)}
              disabled={loadingId === t.id}
              className="btn-desert mt-10 w-full justify-center"
              data-testid={`book-toptier-${t.id}`}
            >
              {loadingId === t.id ? <><Loader2 size={16} className="animate-spin" /> Redirecting</> : <><CreditCard size={16} /> Book this package</>}
            </button>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const DayInLife = () => {
  const [loading, setLoading] = useState(false);
  const p = DAY_IN_LIFE;

  const book = async () => {
    if (p.waveLink) {
      window.location.href = p.waveLink;
      return;
    }
    setLoading(true);
    try {
      const { url } = await createCheckoutSession(p.id);
      if (url) window.location.href = url;
      else throw new Error("no url");
    } catch {
      toast.error("Couldn't start checkout. Please try again or contact us.");
      setLoading(false);
    }
  };

  const fmt = (a) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(a);

  return (
    <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10" data-testid="day-in-life">
      <div className="grid gap-px border border-border md:grid-cols-12">
        <div className="bg-surface p-10 md:col-span-7 lg:p-14">
          <p className="overline text-sand-700">Documentary Project — {p.tagline}</p>
          <ul className="mt-8 space-y-5">
            {p.includes.map((f) => (
              <li key={f} className="flex items-start gap-4 border-b border-border pb-5 font-body text-lg text-sand-100">
                <Check size={18} className="mt-1 shrink-0 text-sand-700" /> {f}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col justify-center bg-[#1d1815] p-10 md:col-span-5 lg:p-14">
          <h2 className="font-display text-5xl text-sand-100 md:text-6xl">{p.name}</h2>
          <p className="mt-6 font-display text-6xl text-sand-100">{fmt(p.price)}</p>
          <p className="mt-6 font-body leading-relaxed text-sand-400">{p.desc}</p>
          <button onClick={book} disabled={loading} className="btn-desert mt-10 w-full justify-center" data-testid="book-day-in-life">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Redirecting</> : <><CreditCard size={16} /> {p.waveLink ? "Book with Wave" : "Book this project"}</>}
          </button>
        </div>
      </div>
    </section>
  );
};

const ContentPartner = () => {
  const [loading, setLoading] = useState(false);
  const [subLink, setSubLink] = useState(null);
  const p = CONTENT_PARTNER;

  useEffect(() => {
    getPaymentConfig().then((c) => setSubLink(c.content_partner_link || null)).catch(() => {});
  }, []);

  const start = async () => {
    // Preferred: recurring Stripe subscription Payment Link (auto-charges monthly)
    if (subLink) {
      window.location.href = subLink;
      return;
    }
    // Fallback: charge the first month online via one-time checkout
    setLoading(true);
    try {
      const { url } = await createCheckoutSession(p.id);
      if (url) window.location.href = url;
      else throw new Error("no url");
    } catch {
      toast.error("Couldn't start checkout. Please try again or contact us.");
      setLoading(false);
    }
  };

  const fmt = (a) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(a);

  return (
    <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10" data-testid="content-partner">
      <div className="grid gap-px border border-border md:grid-cols-12">
        <div className="bg-[#1d1815] p-10 md:col-span-5 lg:p-14">
          <span className="flex items-center gap-2 border border-sand-700 px-3 py-1 text-[0.62rem] uppercase tracking-widest text-sand-700 w-fit">
            <Star size={11} fill="currentColor" /> Ongoing partnership
          </span>
          <h2 className="mt-8 font-display text-4xl text-sand-100 md:text-5xl">{p.name}</h2>
          <div className="mt-6 flex items-end gap-2">
            <span className="font-display text-6xl text-sand-100">{fmt(p.price)}</span>
            <span className="mb-2 font-body text-sand-400">{p.cadence}</span>
          </div>
          <p className="mt-6 font-body font-light leading-relaxed text-sand-400">{p.desc}</p>
          <button onClick={start} disabled={loading} className="btn-desert mt-10 w-full justify-center" data-testid="book-content-partner">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Redirecting</> : <><CreditCard size={16} /> Become a partner</>}
          </button>
          <p className="mt-4 text-xs text-sand-400/70">
            {subLink
              ? "Billed $1,025/month automatically · 12-month agreement."
              : "First month billed today · 12-month agreement · billed monthly thereafter."}
          </p>
        </div>
        <div className="bg-surface p-10 md:col-span-7 lg:p-14">
          <p className="overline text-sand-700">{p.tagline}</p>
          <ul className="mt-8 space-y-5">
            {p.includes.map((f) => (
              <li key={f} className="flex items-start gap-4 border-b border-border pb-5 font-body text-lg font-light text-sand-100">
                <Check size={18} className="mt-1 shrink-0 text-sand-700" /> {f}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

const WeddingPackages = () => {
  const [loadingId, setLoadingId] = useState(null);

  const book = async (w) => {
    if (w.waveLink) {
      window.location.href = w.waveLink;
      return;
    }
    setLoadingId(w.id);
    try {
      const { url } = await createCheckoutSession(w.id);
      if (url) window.location.href = url;
      else throw new Error("no url");
    } catch {
      toast.error("Couldn't start checkout. Please try again or contact us.");
      setLoadingId(null);
    }
  };

  const fmt = (a) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(a);

  return (
    <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-40" data-testid="wedding-packages">
      <Reveal className="mb-16 max-w-2xl">
        <p className="overline text-sand-700">Weddings</p>
        <h2 className="mt-6 font-display text-5xl text-sand-100 md:text-7xl">Wedding packages</h2>
        <p className="mt-6 font-body text-lg font-light text-sand-400">
          Four ways to keep the day forever — book securely online through Wave.
        </p>
      </Reveal>

      <div className="grid gap-px border border-border sm:grid-cols-2 lg:grid-cols-4">
        {WEDDING_PACKAGES.map((w, i) => (
          <motion.div
            key={w.id}
            className={`flex flex-col p-8 transition-colors duration-500 lg:p-10 ${w.featured ? "bg-[#1d1815]" : "bg-surface hover:bg-[#1d1815]"}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            data-testid={`wedding-card-${w.id}`}
          >
            <div className="flex items-center justify-between">
              <span className="font-display text-2xl italic text-sand-700">0{i + 1}</span>
              {w.featured && (
                <span className="flex items-center gap-1 border border-sand-700 px-3 py-1 text-[0.62rem] uppercase tracking-widest text-sand-700">
                  <Star size={11} fill="currentColor" /> Most booked
                </span>
              )}
            </div>
            <h3 className="mt-6 font-display text-4xl text-sand-100">{w.name}</h3>
            <p className="mt-4 font-display text-5xl text-sand-100">{fmt(w.price)}</p>
            <p className="mt-4 font-body text-sm italic text-sand-400">{w.tagline}</p>
            <ul className="mt-8 flex-1 space-y-3">
              {w.includes.map((f) => (
                <li key={f} className="flex items-start gap-3 font-body text-sm font-light text-sand-100/90">
                  <Check size={16} className="mt-0.5 shrink-0 text-sand-700" /> {f}
                </li>
              ))}
            </ul>
            {w.excludes && <p className="mt-5 text-xs uppercase tracking-widest text-sand-400/70">— {w.excludes}</p>}
            <button
              onClick={() => book(w)}
              disabled={loadingId === w.id}
              className="btn-desert mt-8 w-full justify-center"
              data-testid={`book-wedding-${w.id}`}
            >
              {loadingId === w.id ? <><Loader2 size={16} className="animate-spin" /> Redirecting</> : <><CreditCard size={16} /> {w.waveLink ? "Book with Wave" : "Book online"}</>}
            </button>
          </motion.div>
        ))}
      </div>
      <p className="mt-6 text-xs text-sand-400/70">
        Payments processed securely via Wave. Questions or a custom package?{" "}
        <Link to="/contact" className="text-sand-700 link-underline">Contact us</Link> and we&apos;ll set it up.
      </p>
    </section>
  );
};

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHeader
        overline="Services"
        title={<>What we <span className="italic text-sand-700">craft</span></>}
        sub="From first frame to final grade, every service is built around a single question — does it move people?"
      />

      <section className="mx-auto max-w-7xl px-6 pb-28 lg:px-10">
        <div className="border-t border-border">
          {SERVICES.map((s, i) => (
            <Reveal key={s.no}>
              <div className="group grid items-center gap-8 border-b border-border py-12 md:grid-cols-12" data-testid={`service-row-${s.no}`}>
                <div className="md:col-span-1"><span className="font-display text-3xl italic text-sand-700">{s.no}</span></div>
                <div className="md:col-span-4">
                  <h3 className="font-display text-4xl text-sand-100 transition-colors duration-500 group-hover:text-sand-700 md:text-5xl">{s.title}</h3>
                </div>
                <div className="md:col-span-5">
                  <p className="font-body font-light leading-relaxed text-sand-400">{s.desc}</p>
                </div>
                <div className="md:col-span-2">
                  <div className="flex flex-wrap gap-2">
                    {s.tags.map((t) => (
                      <span key={t} className="border border-border px-3 py-1 text-xs uppercase tracking-widest text-sand-400">{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <ContentPartner />

      <TopTier />

      <DayInLife />

      <AddOns />

      <WeddingPackages />

      <EditorialMarquee />

      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10" data-testid="process">
        <div className="grid gap-16 md:grid-cols-12">
          <div className="md:col-span-5">
            <ImageReveal src={IMAGES.bts} alt="Film crew on location in the desert" className="aspect-[3/4]" />
          </div>
          <div className="md:col-span-7">
            <Reveal><p className="overline text-sand-700">The Process</p>
              <h2 className="mt-6 font-display text-5xl text-sand-100 md:text-6xl">Four movements, one film</h2>
            </Reveal>
            <div className="mt-12 space-y-10">
              {PROCESS.map((p, i) => (
                <Reveal key={p.no} delay={i * 0.08}>
                  <div className="flex gap-8 border-t border-border pt-8">
                    <span className="font-display text-2xl italic text-sand-700">{p.no}</span>
                    <div>
                      <h4 className="font-display text-3xl text-sand-100">{p.title}</h4>
                      <p className="mt-2 font-body font-light text-sand-400">{p.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
        <Reveal className="mt-20">
          <Link to="/contact"><span className="btn-desert" data-testid="services-cta">Start your project <ArrowUpRight size={16} /></span></Link>
        </Reveal>
      </section>
    </div>
  );
}
